/**
 * errorLogger.js — Client-side error logging to Firestore via callable function
 */
import { functions, db } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";

const logErrorFn = httpsCallable(functions, "logError");

/**
 * Log an error to the systemHealth/errors collection
 */
export async function logError(error, context = {}) {
  try {
    await logErrorFn({
      severity: "error",
      message: error?.message || String(error),
      stack: error?.stack || null,
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...context,
      },
    });
  } catch (e) {
    // Fail silently — don't let logging cause more errors
    console.error("Failed to log error:", e);
  }
}

/**
 * Log a critical error (e.g., ErrorBoundary catches)
 */
export async function logCritical(error, context = {}) {
  try {
    await logErrorFn({
      severity: "critical",
      message: error?.message || String(error),
      stack: error?.stack || null,
      componentStack: context.componentStack || null,
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...context,
      },
    });
  } catch (e) {
    console.error("Failed to log critical error:", e);
  }
}

/**
 * Log a warning
 */
/**
 * Log a Firestore permission-denied error (mirrors the 404 pattern)
 * Writes to feedback + admin notifications + systemHealth
 */
export async function logPermissionDenied({ uid, email, userName, page, error } = {}) {
  const resolvedUid = uid || "anonymous";
  const resolvedUser = userName || email || "Anonymous";
  const resolvedPage = page || window.location.href;
  const errorMsg = error?.message || "Missing or insufficient permissions";

  // Throttle: skip if this user hit this page within 1 hour
  const throttleKey = `perm-denied-${resolvedUid}-${resolvedPage}`;
  const lastLogged = sessionStorage.getItem(throttleKey);
  if (lastLogged && Date.now() - parseInt(lastLogged, 10) < 3600000) return;

  // 1. Log to feedback collection
  try {
    await addDoc(collection(db, "feedback"), {
      feedbackType: "permission-denied",
      autoLogged: true,
      message: `Permission denied: ${resolvedPage}`,
      page: resolvedPage,
      pageTitle: "Permission Denied",
      referrer: document.referrer || null,
      uid: resolvedUid,
      submittedBy: email || "anonymous",
      submittedByName: resolvedUser,
      isTesterFeedback: false,
      status: "open",
      deviceInfo: navigator.userAgent,
      errorDetail: errorMsg,
      dateSubmitted: serverTimestamp(),
    });
    sessionStorage.setItem(throttleKey, String(Date.now()));

    // Write in-app notification to all admin users
    const adminsSnap = await getDocs(
      query(collection(db, "users"), where("role", "==", "admin"))
    );
    await Promise.all(
      adminsSnap.docs.map((adminDoc) =>
        addDoc(collection(db, "users", adminDoc.id, "notifications"), {
          type: "system",
          title: "Permission Denied",
          body: `${resolvedUser} hit permission error on ${resolvedPage}`,
          icon: "ShieldAlert",
          link: "/admin/system/overview",
          read: false,
          createdAt: serverTimestamp(),
        })
      )
    );
  } catch (e) {
    console.error("Failed to log permission-denied feedback:", e);
  }

  // 2. Log to Error Feed via Cloud Function
  logWarning(`Permission Denied: ${resolvedPage}`, {
    type: "permission-denied",
    page: resolvedPage,
    referrer: document.referrer || null,
    user: resolvedUser,
    uid: resolvedUid,
    userAgent: navigator.userAgent,
    errorMessage: errorMsg,
  }).catch(() => {});
}

/**
 * Log a warning
 */
export async function logWarning(message, context = {}) {
  try {
    await logErrorFn({
      severity: "warning",
      message: String(message),
      stack: null,
      context: {
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...context,
      },
    });
  } catch (e) {
    console.error("Failed to log warning:", e);
  }
}
